import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import Plot from "react-plotly.js";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";

import type { Theme } from "src/theme";
import { overviewSelector } from "src/store/dashboard/selector";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import {
  type TimeOption,
  getPeriodFromPreset,
} from "src/components/TimeFilter";
import { Title } from "src/components/TableHeader";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import {
  ThemedSvgGradientDef,
  makeBarFill,
  useChartColors,
} from "src/components/SvgGradientDef";
import { formatDollars } from "src/utils/formatter";
import { useRecordEvent } from "src/hooks/useRecordEvent";

import Card from "./Card";
import { getTourOpen } from "src/store/tour/selector";

const useStyles = makeStyles((theme: Theme) => {
  const { custom: palette } = theme.palette.custom;

  const gradients = {
    Green: { name: "Green", color: palette.brandSecondary, stops: [] },
    Blue: { name: "Blue", color: palette.brandPrimary, stops: [] },
  };

  return {
    container: {
      "& > * + *": {
        marginLeft: theme.spacing(2),
      },
      "& > *": {
        width: "25%",
      },
      display: "flex",
      marginBottom: theme.spacing(2),
      justifyContent: "space-between",
    },
    plot: ({ bars }: { bars: ("up" | "down")[][] }) => {
      const styles = {
        ...makeBarFill([gradients.Green, gradients.Blue]),
      };

      for (let i = 0; i < bars.length; i++) {
        const bar = bars[i];
        for (let ii = 0; ii < bar.length; ii++) {
          styles[
            `& g.cartesianlayer g.plot g.barlayer.mlayer g.trace.bars:nth-child(${
              i + 1
            }) g.points g.point:nth-child(${ii + 1}) path`
          ] = {
            clipPath:
              bar[ii] === "up"
                ? "inset(0% 0% 0% 0% round 8px 8px 0px 0px)"
                : "inset(0% 0% 0% 0% round 0px 0px 8px 8px)",
          };
        }
      }

      return styles;
    },
  };
});

export interface OverviewProps {
  timeFilter: TimeOption;
}

function Overview({ timeFilter }: OverviewProps) {
  const recordEvent = useRecordEvent();

  const history = useHistory();
  const theme = useTheme();
  const chartColors = useChartColors();
  const tourActive = useSelector(getTourOpen);
  const timeZone = useSelector(effectiveUserTimeZoneSelector);

  // Force chart to recalculate layout after CSS transitions
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "width") {
        setRevision((r) => r + 1);
      }
    };

    // Listen for transition end on the main content area
    document.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      document.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, []);

  const periodDates = useMemo(() => {
    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);
    return {
      start: period.start,
      end: period.end,
    };
  }, [timeFilter, timeZone]);

  const period = useMemo(
    () => ({
      start: calendarYmdInTimeZone(periodDates.start, timeZone),
      end: calendarYmdInTimeZone(periodDates.end, timeZone),
    }),
    [periodDates, timeZone]
  );

  const { income, expenses, profit, chart } = useSelector((state) =>
    overviewSelector(state, period)
  );
  const classes = useStyles({
    bars: [chart.income, chart.expenses].map((b) =>
      b.map((v) => (v > 0 ? "up" : "down"))
    ),
  });

  const handleCardClick = (
    card: "income" | "expenses" | "profit" | "profit_percent"
  ) => {
    recordEvent({
      type: "feature-adoption",
      feature: "dashboard-card-click",
      card,
      timeFilter: timeFilter.type,
    });
    const params = new URLSearchParams({
      startDate: periodDates.start.toISOString(),
      endDate: periodDates.end.toISOString(),
      reportType: "profit/loss",
      autoRun: "true",
    });
    history.push(`/reports?${params.toString()}`);
  };

  return (
    <Box id={"dashboards-overview-cards"}>
      <div className={classes.container}>
        <Card
          title="Income"
          content={`${formatDollars(income)}`}
          onClick={() => handleCardClick("income")}
        />
        <Card
          title="Expenses"
          content={`${formatDollars(expenses)}`}
          onClick={() => handleCardClick("expenses")}
        />
        <Card
          title="Profit ($)"
          content={`${formatDollars(profit)}`}
          onClick={() => handleCardClick("profit")}
          highlighted={tourActive}
        />
        <Card
          title="Profit (%)"
          content={`${(income ? (profit * 100) / income : 0).toFixed(2)}%`}
          onClick={() => handleCardClick("profit_percent")}
          highlighted={tourActive}
        />
      </div>
      <ThemedSvgGradientDef />
      <Box
        component={Paper}
        display="flex"
        flexDirection="column"
        height="auto"
        p={2.5}
      >
        <Title>Profitability</Title>
        <Plot
          className={classes.plot}
          revision={revision}
          data={[
            {
              x: chart.time,
              y: chart.income,
              type: "bar",
              marker: { color: chartColors.Green },
              name: "Income",
              hovertemplate: "Income<br />%{x}: %{y:$,.2f}<extra></extra>",
            },
            {
              x: chart.time,
              y: chart.expenses,
              type: "bar",
              marker: { color: chartColors.Blue },
              name: "Expenses",
              hovertemplate: "Expenses<br />%{x}: %{y:$,.2f}<extra></extra>",
            },
            {
              x: chart.time,
              y: chart.profit,
              type: "scatter",
              mode: "lines",
              line: { shape: "spline" },
              marker: { color: theme.palette.grey[400] },
              name: "Profit",
              hovertemplate: "Profit<br />%{x}: %{y:$,.2f}<extra></extra>",
            },
          ]}
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: 450,
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: {
              color: theme.palette.text.primary,
            },
            xaxis: {
              gridcolor: theme.palette.divider,
              zerolinecolor: theme.palette.divider,
              showgrid: false,
            },
            yaxis: {
              tickprefix: "$",
              gridcolor: theme.palette.divider,
              zerolinecolor: theme.palette.divider,
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default Overview;
